/**
 * @fileoverview Predictive Search Implementation
 * @description Implements a predictive search system that provides real-time search results as users type
 *
 * Features:
 * - Real-time search results
 * - Dynamic height calculation for search results
 * - Keyboard navigation support
 * - Promotional content display
 * - Responsive design support
 *
 * HTML Structure Required:
 * - Element with data-predictive-search attribute for results container
 * - Search input field
 * - Search form
 * - Optional promotional content block
 */

class PredictiveSearch extends SearchForm {
  /**
   * @constructor
   * @description Initializes the predictive search functionality
   */
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]'
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.body = document.querySelector('body');
    this.headerBottom = document.querySelector('.header__bottom');
    this.promoModal = document.querySelector('.search__modal');
    this.headerInput = document.querySelector('.header__search .search__input');
    this.calcHeight();
    this.setupEventListeners();

    const searchResizeObserver = new ResizeObserver((entries) => {
      const [entry] = entries;

      if (entry.contentRect.width < 991) {
        this.calcHeight(65);
      } else {
        this.calcHeight(0);
      }
    });

    searchResizeObserver.observe(this.body);
  }

  /**
   * @function calcHeight
   * @description Calculates and sets the maximum height for the search results container
   * @param {number} buttonHeight - Additional height to account for buttons
   */
  calcHeight(buttonHeight) {
    let heightAnnouncementBar = document.querySelector('.section-announcement')
      ? document.querySelector('.section-announcement').getBoundingClientRect()
          .height
      : 0;
    let heightHeader = document.querySelector('.shopify-section-header')
      ? document
          .querySelector('.shopify-section-header')
          .getBoundingClientRect().height
      : 0;
    let heightSearchTop = document.querySelector('.search-modal__top')
      ? document.querySelector('.search-modal__top').getBoundingClientRect()
          .height
      : 0;
    let heightInput = this.headerInput.getBoundingClientRect().height;

    if (document.querySelector('.predictive-search')) {
      let maxHeightContent = parseFloat(
        window
          .getComputedStyle(document.querySelector('.predictive-search'), null)
          .getPropertyValue('max-height')
      );
      let heightContent = document
        .querySelector('.predictive-search')
        .getBoundingClientRect().height;

      let clientHeight = document.documentElement.clientHeight;
      let maxHeightAllowed =
        clientHeight -
        heightAnnouncementBar -
        heightHeader -
        heightSearchTop -
        heightInput -
        buttonHeight -
        10;
      document.querySelector(
        '.predictive-search'
      ).style.maxHeight = `${maxHeightAllowed}px`;
    }
  }

  /**
   * @function setupEventListeners
   * @description Sets up all necessary event listeners for the search functionality
   */
  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  /**
   * @function getQuery
   * @description Gets the current search query from the input field
   * @returns {string} The trimmed search query
   */
  getQuery() {
    return this.input.value.trim();
  }

  /**
   * @function onChange
   * @description Handles changes to the search input and triggers search updates
   */
  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  /**
   * @function onFormSubmit
   * @description Handles form submission events
   * @param {Event} event - The form submit event
   */
  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a') ||
      event.submitter.classList.contains('card__link')
    )
      event.preventDefault();
  }

  /**
   * @function onFormReset
   * @description Handles form reset events
   * @param {Event} event - The form reset event
   */
  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  /**
   * @function onFocus
   * @description Handles focus events on the search input
   */
  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (this.classList.contains('search-modal__form')) {
      if (this.headerBottom) {
        this.headerBottom.classList.add('header__bottom--visible');
      }
      if (this.promoModal) {
        this.promoModal.classList.remove('search__modal--hidden');
      }
    }

    if (!currentSearchTerm.length) {
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
      if (this.promoModal) {
        this.promoModal.classList.add('search__modal--hidden');
      }
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  /**
   * @function onFocusOut
   * @description Handles focus out events on the search input
   */
  onFocusOut() {
    if (this.classList.contains('search-modal__form')) {
      if (this.headerBottom) {
        this.headerBottom.classList.remove('header__bottom--visible');
      }
    }
    if (!this.classList.contains('search-modal__form')) {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }
  }

  /**
   * @function onBlur
   * @description Handles blur events on the search input
   */
  onBlur() {
    this.headerInput.blur();
  }

  /**
   * @function onKeyup
   * @description Handles keyup events for keyboard navigation and search control
   * @param {KeyboardEvent} event - The keyboard event
   */
  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      //case "ArrowUp":
      //	this.switchOption("up");
      //	break;
      //case "ArrowDown":
      //	this.switchOption("down");
      //	break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
      case 'Escape':
        this.closeResults(true);
        if (this.promoModal && this.classList.contains('search-modal__form')) {
          this.promoModal.classList.add('search__modal--hidden');
        }
        break;
    }

    if (this.getQuery().length) {
      this.hidePromoBlock();
    } else {
      this.showPromoBlock();
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  /**
   * @function updateSearchForTerm
   * @description Updates the search results for a new search term
   * @param {string} previousTerm - The previous search term
   * @param {string} newTerm - The new search term
   */
  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (
        currentButtonText.matchAll(new RegExp(previousTerm, 'g')).length > 1
      ) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
    if (this.getQuery().length) {
      this.hidePromoBlock();
    } else {
      this.showPromoBlock();
    }
  }

  //switchOption(direction) {
  //	if (!this.getAttribute("open")) return;

  //	const moveUp = direction === "up";
  //	const selectedElement = this.querySelector('[aria-selected="true"]');

  //	// Filter out hidden elements (duplicated page and article resources) thanks
  //	// to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
  //	const allVisibleElements = Array.from(
  //		this.querySelectorAll("li, button.predictive-search__item"),
  //	).filter((element) => element.offsetParent !== null);
  //	let activeElementIndex = 0;

  //	if (moveUp && !selectedElement) return;

  //	let selectedElementIndex = -1;
  //	let i = 0;

  //	while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
  //		if (allVisibleElements[i] === selectedElement) {
  //			selectedElementIndex = i;
  //		}
  //		i++;
  //	}

  //	this.statusElement.textContent = "";

  //	if (!moveUp && selectedElement) {
  //		activeElementIndex =
  //			selectedElementIndex === allVisibleElements.length - 1
  //				? 0
  //				: selectedElementIndex + 1;
  //	} else if (moveUp) {
  //		activeElementIndex =
  //			selectedElementIndex === 0
  //				? allVisibleElements.length - 1
  //				: selectedElementIndex - 1;
  //	}

  //	if (activeElementIndex === selectedElementIndex) return;

  //	const activeElement = allVisibleElements[activeElementIndex];

  //	activeElement.setAttribute("aria-selected", true);
  //	if (selectedElement) selectedElement.setAttribute("aria-selected", false);

  //	this.input.setAttribute("aria-activedescendant", activeElement.id);
  //}

  /**
   * @function selectOption
   * @description Handles selection of a search result option
   */
  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  /**
   * @function getSearchResults
   * @description Fetches search results for the given search term
   * @param {string} searchTerm - The term to search for
   */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.clickSearchTabs();
      this.calcHeight();
      return;
    }

    if (this.promoModal && this.classList.contains('search-modal__form')) {
      this.promoModal.classList.add('search__modal--hidden');
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
        this.clickSearchTabs();
        this.calcHeight();
        try {
          colorSwatches();
        } catch (err) {}
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  /**
   * @function setLiveRegionLoadingState
   * @description Updates the live region to indicate loading state
   */
  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  /**
   * @function setLiveRegionText
   * @description Updates the live region with status text
   * @param {string} statusText - The status text to display
   */
  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  /**
   * @function renderSearchResults
   * @description Renders the search results in the DOM
   * @param {string} resultsMarkup - The HTML markup for the results
   */
  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    if (this.promoModal && this.classList.contains('search-modal__form')) {
      this.promoModal.classList.add('search__modal--hidden');
    }
  }

  /**
   * @function clickSearchTabs
   * @description Handles click events on search result tabs
   */
  clickSearchTabs() {
    const results = this.querySelectorAll('.predictive-search__results-list');
    if (results.length != 0) {
      results[0].classList.add('active');
      this.querySelector('.predictive-search__result-tab').classList.add(
        'active'
      );
      this.querySelectorAll('.predictive-search__result-tab').forEach((tab) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          const typeTarget = tab.dataset.typeTarget;
          this.querySelectorAll('.predictive-search__result-tab').forEach(
            (element) => {
              element.classList.remove('active');
            }
          );
          tab.classList.add('active');

          results.forEach((element) => {
            let resultsType = element.dataset.type;
            if (resultsType == typeTarget) {
              element.classList.add('active');
            } else {
              element.classList.remove('active');
            }
          });
        });
      });
    }
  }

  /**
   * @function hidePromoBlock
   * @description Hides the promotional content block
   */
  hidePromoBlock() {
    document.querySelector('.search__categories')
      ? document.querySelector('.search__categories').classList.add('hide')
      : false;
    document.querySelector('.search__promo-block')
      ? document.querySelector('.search__promo-block').classList.add('hide')
      : false;
    document.querySelector('.search__line')
      ? document.querySelector('.search__line').classList.add('hide')
      : false;
  }

  /**
   * @function showPromoBlock
   * @description Shows the promotional content block
   */
  showPromoBlock() {
    document.querySelector('.search__categories')
      ? document.querySelector('.search__categories').classList.remove('hide')
      : false;
    document.querySelector('.search__promo-block')
      ? document.querySelector('.search__promo-block').classList.remove('hide')
      : false;
    document.querySelector('.search__line')
      ? document.querySelector('.search__line').classList.remove('hide')
      : false;
  }

  /**
   * @function setLiveRegionResults
   * @description Updates the live region with search results
   */
  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')
        .textContent
    );
  }

  //getResultsMaxHeight() {
  //	this.resultsMaxHeight =
  //		window.innerHeight -
  //		document.querySelector(".shopify-section-header").getBoundingClientRect()
  //			.bottom;
  //	return this.resultsMaxHeight;
  //}

  /**
   * @function open
   * @description Opens the search results dropdown
   */
  open() {
    //this.predictiveSearchResults.style.maxHeight =
    //	this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    if (this.promoModal && this.classList.contains('search-modal__form')) {
      this.promoModal.classList.remove('search__modal--hidden');
    }
  }

  /**
   * @function close
   * @description Closes the search results dropdown
   * @param {boolean} clearSearchTerm - Whether to clear the search input
   */
  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  /**
   * @function closeResults
   * @description Closes the search results
   * @param {boolean} clearSearchTerm - Whether to clear the search input
   */
  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    //this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    if (this.promoModal) {
      this.promoModal.classList.add('search__modal--hidden');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
